import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuditService } from '../../core/services/audit.service';
import { AuditLogResponse } from '../../core/models/models';
import { ModalComponent } from '../../shared/components/modal/modal.component';

@Component({
  selector: 'app-audit-logs',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent],
  template: `
    <div class="space-y-6 font-sans">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-extrabold text-slate-900 tracking-tight">Security & System Audit Trail</h1>
          <p class="text-xs text-slate-500">Immutable audit log stream of network transactions, DSR authorizations, and role changes.</p>
        </div>
      </div>

      <!-- Search & Filters -->
      <div class="glass-panel p-4 rounded-2xl border border-slate-200 bg-white flex items-center justify-between gap-4 shadow-sm">
        <input
          type="text"
          [(ngModel)]="searchQuery"
          placeholder="Filter by event name or URL endpoint..."
          class="px-4 py-2 rounded-xl bg-white border border-slate-300 text-xs text-slate-900 placeholder-slate-400 w-80 focus:border-emerald-500"
        />

        <div class="text-xs text-slate-500 font-mono font-semibold">
          Security Events Recorded: <span class="text-emerald-700 font-bold">{{ filteredAudits().length }}</span>
        </div>
      </div>

      <!-- Audit Logs Table -->
      <div class="glass-panel rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-sm">
        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse text-xs">
            <thead>
              <tr class="bg-slate-100/80 text-slate-600 uppercase tracking-wider font-bold border-b border-slate-200">
                <th class="p-4">Log ID</th>
                <th class="p-4">Timestamp</th>
                <th class="p-4">User Type</th>
                <th class="p-4">Event Code</th>
                <th class="p-4">Entity Type & ID</th>
                <th class="p-4">IP Address</th>
                <th class="p-4 text-right">Details</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200/80">
              @for (log of filteredAudits(); track log.id) {
                <tr class="hover:bg-emerald-50/30 transition-colors font-mono">
                  <td class="p-4 text-emerald-700 font-bold">#{{ log.id }}</td>
                  <td class="p-4 text-slate-600 text-[11px] font-medium">{{ log.createdAt }}</td>
                  <td class="p-4 text-slate-800 font-bold">{{ log.userType }} (ID: {{ log.userId }})</td>
                  <td class="p-4 font-sans">
                    <span class="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
                      {{ log.event }}
                    </span>
                  </td>
                  <td class="p-4 text-slate-600 text-[11px]">{{ log.auditableType }} #{{ log.auditableId }}</td>
                  <td class="p-4 text-slate-600 text-[11px]">{{ log.ipAddress }}</td>
                  <td class="p-4 text-right font-sans">
                    <button
                      (click)="openInspector(log)"
                      class="px-3 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-600 hover:text-white font-bold text-emerald-700 text-xs transition-colors border border-emerald-200 shadow-2xs"
                    >
                      Inspect JSON
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>

      <!-- JSON Inspector Modal -->
      <app-modal
        [isOpen]="showInspectorModal()"
        [title]="'Audit Log Inspector: Log #' + (selectedLog()?.id || '')"
        (close)="showInspectorModal.set(false)"
      >
        @if (selectedLog(); as log) {
          <div class="space-y-4 text-xs">
            <div class="grid grid-cols-2 gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div>
                <span class="text-slate-500 block">HTTP Request URL:</span>
                <span class="font-mono text-emerald-800 font-bold">{{ log.url }}</span>
              </div>
              <div>
                <span class="text-slate-500 block">User Agent:</span>
                <span class="font-mono text-slate-700 text-[11px] truncate block">{{ log.userAgent }}</span>
              </div>
            </div>

            <div>
              <span class="font-bold text-slate-700 uppercase block mb-1">Previous Values (State Before):</span>
              <pre class="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 font-mono text-[11px] overflow-x-auto">{{ log.oldValues || 'null' }}</pre>
            </div>

            <div>
              <span class="font-bold text-slate-700 uppercase block mb-1">New Values (State After):</span>
              <pre class="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 font-mono text-[11px] overflow-x-auto">{{ log.newValues || 'null' }}</pre>
            </div>

            <div class="flex justify-end pt-2 border-t border-slate-200">
              <button
                (click)="showInspectorModal.set(false)"
                class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300"
              >
                Close Inspector
              </button>
            </div>
          </div>
        }
      </app-modal>
    </div>
  `
})
export class AuditLogsComponent implements OnInit {
  auditService = inject(AuditService);

  audits = signal<AuditLogResponse[]>([]);
  searchQuery = '';

  showInspectorModal = signal<boolean>(false);
  selectedLog = signal<AuditLogResponse | null>(null);

  filteredAudits = computed(() => {
    let list = this.audits();
    if (this.searchQuery) {
      const q = this.searchQuery.toLowerCase();
      list = list.filter(a => a.event.toLowerCase().includes(q) || (a.url && a.url.toLowerCase().includes(q)));
    }
    return list;
  });

  ngOnInit() {
    this.auditService.getAllAudits().subscribe(res => {
      if (res.success && res.data) this.audits.set(res.data);
    });
  }

  openInspector(log: AuditLogResponse) {
    this.selectedLog.set(log);
    this.showInspectorModal.set(true);
  }
}
